// ===============================================
// File: src/components/Widgets/UtilitiesWidget.jsx
// Local-only, charted utilities widget (no Icon import)
// - Safer currency formatter (memoized + fallback)
// - Stable keys for rows (no random churn)
// - Minor a11y polish on the donut
// ===============================================

import React, { useContext, useState, useMemo, useEffect } from "react";
import { UtilitiesContext } from "../../contexts/UtilitiesContext";
import WidgetHeader from "../Common/WidgetHeader";

const LS_USER = "mudoUserSettings";
const LS_PROD = "mudoProductionSettings";

// ---- legacy helpers for expected units (fallback only) ----
function readExpectedUnitsFromLS() {
  const safe = (k) => {
    try { return JSON.parse(localStorage.getItem(k)); } catch { return null; }
  };
  const user = safe(LS_USER) || {};
  const prod = safe(LS_PROD) || {};
  return Number(prod?.productionOutput ?? user?.productionOutput ?? 0) || 0;
}
function writeExpectedUnitsToLS(units) {
  try {
    const prod = JSON.parse(localStorage.getItem(LS_PROD) || "{}");
    prod.productionOutput = units;
    localStorage.setItem(LS_PROD, JSON.stringify(prod));
  } catch {}
}

// Converters
const toWeekly = (u) => {
  const c = Number(u?.cost ?? 0) || 0;
  switch (String(u?.frequency || "").toLowerCase()) {
    case "weekly": return c;
    case "fortnightly": return c / 2;
    case "monthly": return c / 4.333;
    case "quarterly": return c / 13;
    case "yearly": return c / 52;
    default: return 0;
  }
};
const toMonthly = (u) => {
  const c = Number(u?.cost ?? 0) || 0;
  switch (String(u?.frequency || "").toLowerCase()) {
    case "weekly": return c * 4.333;
    case "fortnightly": return c * 2.166;
    case "monthly": return c;
    case "quarterly": return c / 3;
    case "yearly": return c / 12;
    default: return 0;
  }
};
const toYearly = (u) => {
  const c = Number(u?.cost ?? 0) || 0;
  switch (String(u?.frequency || "").toLowerCase()) {
    case "weekly": return c * 52;
    case "fortnightly": return c * 26;
    case "monthly": return c * 12;
    case "quarterly": return c * 4;
    case "yearly": return c;
    default: return 0;
  }
};

// Stable key for rows (falls back to name+category)
const rowKey = (u) =>
  String(u?.id ?? u?.name ?? `${u?.type || u?.category || "util"}_${u?.cost}_${u?.frequency}`);

const makeFormatter = (currency = "AUD") => {
  try {
    return new Intl.NumberFormat("en-AU", { style: "currency", currency, maximumFractionDigits: 2 });
  } catch {
    return { format: (v) => `A$${Number(v || 0).toFixed(2)}` };
  }
};

export default function UtilitiesWidget({
  onOpenModal,
  expectedUnits,         // optional prop override
  onExpectedUnitsChange, // optional callback when user edits units
  backTo = "/dashboard",
  settings,              // pass settings for currency + productionOutput
}) {
  const ctx = useContext(UtilitiesContext) || {};
  const utilities = Array.isArray(ctx.utilities) ? ctx.utilities : [];

  const [period, setPeriod] = useState("Monthly");

  // Expected units: prop → settings → legacy local
  const hasPropUnits =
    typeof expectedUnits === "number" && !Number.isNaN(expectedUnits);

  const settingsUnitsRaw =
    typeof settings?.productionOutput === "number"
      ? settings.productionOutput
      : Number(settings?.productionOutput || 0);

  const hasSettingsUnits =
    typeof settingsUnitsRaw === "number" && !Number.isNaN(settingsUnitsRaw);

  const [unitsLocal, setUnitsLocal] = useState(() => readExpectedUnitsFromLS());

  const units = hasPropUnits
    ? expectedUnits
    : hasSettingsUnits
    ? settingsUnitsRaw
    : unitsLocal;

  useEffect(() => {
    if (!hasPropUnits && !hasSettingsUnits) {
      writeExpectedUnitsToLS(unitsLocal);
    }
  }, [unitsLocal, hasPropUnits, hasSettingsUnits]);

  const totals = useMemo(
    () =>
      utilities.reduce(
        (acc, u) => {
          acc.weekly += toWeekly(u);
          acc.monthly += toMonthly(u);
          acc.yearly += toYearly(u);
          return acc;
        },
        { weekly: 0, monthly: 0, yearly: 0 }
      ),
    [utilities]
  );

  const rows = useMemo(() => {
    const mapFn =
      period === "Weekly" ? toWeekly : period === "Yearly" ? toYearly : toMonthly;
    return utilities
      .map((u) => ({
        id: rowKey(u),
        name: u?.name || "Untitled",
        category: u?.type || u?.category || "",
        value: mapFn(u),
      }))
      .filter((r) => r.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [utilities, period]);

  const displayTotal =
    period === "Weekly" ? totals.weekly : period === "Yearly" ? totals.yearly : totals.monthly;

  // Donut chart
  const radius = 56;
  const circumference = 2 * Math.PI * radius;
  const sum = rows.reduce((s, r) => s + r.value, 0);

  const colors = [
    "#6EE7B7", "#93C5FD", "#FDE68A", "#FCA5A5", "#C4B5FD",
    "#A5F3FC", "#FBCFE8", "#FCD34D", "#86EFAC", "#FCA5A5",
  ];

  let offset = 0;
  const segments = rows.map((r, i) => {
    const frac = sum > 0 ? r.value / sum : 0;
    const len = frac * circumference;
    const seg = (
      <circle
        key={r.id}
        r={radius}
        cx="75"
        cy="75"
        fill="transparent"
        stroke={colors[i % colors.length]}
        strokeWidth="14"
        strokeDasharray={`${len} ${circumference - len}`}
        strokeDashoffset={-offset}
        strokeLinecap="round"
      />
    );
    offset += len;
    return seg;
  });

  const legend = useMemo(() => {
    const top = rows.slice(0, 5);
    const rest = rows.slice(5);
    const restVal = rest.reduce((s, r) => s + r.value, 0);
    if (rest.length) {
      top.push({
        id: "__other__",
        name: "Other",
        value: restVal,
        category: "",
        color: "#E5E7EB",
      });
    }
    return top.map((r, i) => ({
      ...r,
      color: r.id === "__other__" ? "#E5E7EB" : colors[i % colors.length],
      pct: sum > 0 ? (r.value / sum) * 100 : 0,
    }));
  }, [rows, sum]);

  const setUnits = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n) || n < 0) return;
    if (typeof onExpectedUnitsChange === "function") {
      onExpectedUnitsChange(n);
      return;
    }
    setUnitsLocal(n);
  };

  const currency = settings?.currency || "AUD";
  const fmt = useMemo(() => makeFormatter(currency), [currency]);

  return (
    <div className="rounded-lg p-0 h-full flex flex-col bg-transparent text-inherit">
      <WidgetHeader
        title={
          <span className="flex items-center gap-2">
            <span role="img" aria-label="Utilities"></span>
            <span>Utilities</span>
          </span>
        }
        to={backTo}
        right={
          <div className="flex items-center gap-2">
            {["Weekly", "Monthly", "Yearly"].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-2 py-1 text-xs rounded ${
                  p === period
                    ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                    : "bg-transparent border border-black/10 dark:border-white/10"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={onOpenModal}
              className="px-2 py-1 text-xs rounded text-white transition-colors hover:opacity-90"
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              Manage
            </button>
          </div>
        }
      />

      {/* Totals & Expected Units */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3 px-4">
        <div className="text-sm opacity-80 tabular-nums">
          Total {period}: <strong>{fmt.format(displayTotal)}</strong>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <label className="opacity-80">Expected units / month:</label>
          <input
            type="number"
            min="0"
            value={units}
            onChange={(e) => setUnits(e.target.value)}
            className="w-24 px-2 py-1 rounded border border-black/10 dark:border-white/10 bg-transparent text-right"
            title="This drives per-unit utility cost"
          />
        </div>
      </div>

      {/* Chart + Legend */}
      {rows.length ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center px-4">
          {/* Donut */}
          <div className="flex items-center justify-center">
            <svg
              width="150"
              height="150"
              viewBox="0 0 150 150"
              role="img"
              aria-label={`Utilities cost breakdown (${period})`}
            >
              <circle
                r={radius}
                cx="75"
                cy="75"
                fill="transparent"
                stroke="rgba(0,0,0,0.1)"
                strokeWidth="14"
              />
              <g transform="rotate(-90 75 75)">{segments}</g>
              <text x="75" y="70" textAnchor="middle" fontSize="12" opacity="0.75">
                {period}
              </text>
              <text x="75" y="88" textAnchor="middle" fontSize="13" fontWeight="600" className="tabular-nums">
                {fmt.format(displayTotal)}
              </text>
            </svg>
          </div>

          {/* Legend */}
          <div className="space-y-2">
            {legend.map((r) => (
              <div key={r.id} className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="inline-block w-3 h-3 rounded"
                    style={{ background: r.color }}
                  />
                  <div className="truncate text-sm">
                    {r.name}
                    {r.category ? <span className="opacity-60"> • {r.category}</span> : null}
                  </div>
                </div>
                <div className="text-sm tabular-nums">
                  {fmt.format(r.value)} <span className="opacity-60">({r.pct.toFixed(0)}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center opacity-70 px-4">
          No utilities added yet.
        </div>
      )}

      {/* Per-unit cost footer */}
      <div
        className="mt-4 pt-3 text-sm flex items-center justify-between px-4"
        style={{
          borderTop: "1px solid color-mix(in oklab, currentColor 12%, transparent)",
        }}
      >
        <div className="opacity-80">
          Per-unit utility cost (based on {units || 0} units / month):
        </div>
        <div className="font-semibold tabular-nums">
          {makeFormatter(currency).format(units > 0 ? totals.monthly / units : 0)} / unit
        </div>
      </div>
    </div>
  );
}
